import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Button, Spinner } from "@nike/epic-react-ui";
import { selectAccessToken, selectEmail } from "@nike/oauth-client";
import { requireLogin } from "../auth/LoginRequired";
import "./OwnershipApprovalPage.css";

const SLACKAPP_API =
  window.location.origin.includes("cloudred.nike.com") ||
  window.location.origin.includes("cloudred-prod.nikecloud.com")
    ? "https://1wpv0puuua.execute-api.us-west-2.amazonaws.com/prod"
    : "https://q44igx8ujh.execute-api.us-west-2.amazonaws.com/test";

const CLOUDRED_API = `${window.location.origin}/api`;

function formatApiError(body, status) {
  const detail = body && body.detail;
  if (Array.isArray(detail)) {
    return detail.map(d => d.msg || JSON.stringify(d)).join("; ");
  }
  if (typeof detail === "string") {
    return detail;
  }
  return `HTTP ${status}`;
}

function statusBadgeClass(status) {
  const key = (status || "").toLowerCase().replace(/_/g, "-");
  if (key === "awaiting-nominee") return "ownership-status-badge--approved";
  if (key === "pending") return "ownership-status-badge--pending";
  if (key === "completed") return "ownership-status-badge--completed";
  if (key === "rejected") return "ownership-status-badge--rejected";
  return "ownership-status-badge--expired";
}

const PageHero = ({ subtitle }) => (
  <header className="ownership-hero">
    <h1 className="ownership-hero-title">Ownership Nomination</h1>
    <p className="ownership-hero-subtitle">{subtitle}</p>
  </header>
);

const mapProps = state => ({
  accessToken: selectAccessToken(state),
  email: selectEmail(state)
});

const OwnershipNomineePage = ({ accessToken, email, data }) => {
  const reqId = data && data.reqId;
  const token = (data && data.nomineeToken) || "";

  const [request, setRequest] = useState(null);
  const [systemOwner, setSystemOwner] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!reqId || !token) {
      setError("Missing request id or token in URL.");
      return;
    }
    if (!accessToken) {
      return;
    }
    fetch(
      `${SLACKAPP_API}/ownership-request/${reqId}?t=${encodeURIComponent(
        token
      )}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json"
        }
      }
    )
      .then(async res => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(formatApiError(body, res.status));
        }
        setRequest(body);
      })
      .catch(err => setError(err.message || String(err)));
  }, [reqId, token, accessToken]);

  useEffect(() => {
    if (!request || !accessToken || !request.tagguid) {
      return;
    }
    fetch(`${CLOUDRED_API}/ssot/systems/${request.tagguid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json"
      }
    })
      .then(async res => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok || body.status === "fail" || !body.owner) {
          return;
        }
        setSystemOwner(body.owner);
      })
      .catch(() => {});
  }, [request, accessToken]);

  const postNominee = async suffix => {
    const res = await fetch(
      `${SLACKAPP_API}/ownership-request/${reqId}/${suffix}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          t: token,
          okta_email: email,
          note: note || null
        })
      }
    );
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(formatApiError(body, res.status));
    }
    return body;
  };

  const submit = async accept => {
    if (!email) {
      setError(
        "Your Nike email is not available yet. Wait a moment, refresh, and try again."
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body = await postNominee(
        accept ? "nominee-accept" : "nominee-decline"
      );
      setOutcome({
        status: body.status || (accept ? "PENDING" : "REJECTED"),
        managerEmailSent: body.manager_email_sent
      });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (error && !request) {
    return (
      <div className="ownership-page">
        <PageHero subtitle="CloudRed tagguid ownership nomination" />
        <div className="ownership-center-state">
          <p style={{ color: "#a40000" }}>{error}</p>
        </div>
      </div>
    );
  }

  if (outcome) {
    const accepted = outcome.status === "PENDING";
    const managerEmailSent = outcome.managerEmailSent;
    return (
      <div className="ownership-page">
        <PageHero subtitle="Nomination response recorded" />
        <div className="ownership-center-state">
          <div
            className={`ownership-outcome-icon ${
              accepted
                ? "ownership-outcome-icon--success"
                : "ownership-outcome-icon--error"
            }`}
          >
            {accepted ? "✓" : "—"}
          </div>
          <h2>
            {accepted
              ? "You accepted this ownership nomination"
              : "You declined this nomination"}
          </h2>
          {accepted && request && managerEmailSent === false && (
            <p style={{ color: "#a40000" }}>
              The manager approval email could not be sent. The requester has
              been notified on Slack — contact CloudTools if this persists.
            </p>
          )}
          {accepted && request && managerEmailSent !== false && (
            <p>
              Your manager <strong>{request.approver_email}</strong> will
              receive an approval email. Ownership will change only after they
              approve.
            </p>
          )}
          {!accepted && (
            <p>
              The requester has been notified. No manager approval will be sent.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!request || submitting) {
    return (
      <div className="ownership-page">
        <PageHero subtitle="Loading nomination details…" />
        <div className="ownership-center-state">
          <Spinner title="Loading..." />
        </div>
      </div>
    );
  }

  const notYou =
    email &&
    request.new_owner_email &&
    email.toLowerCase() !== request.new_owner_email.toLowerCase();
  const expired = request.expires_at * 1000 < Date.now();
  const canAct =
    email && !notYou && !expired && request.status === "AWAITING_NOMINEE";
  const currentOwner = systemOwner || request.current_owner_email || "Unknown";

  return (
    <div className="ownership-page">
      <PageHero subtitle="You have been nominated as the new owner. Accept only if you are willing to own this tagguid." />

      <div className="ownership-shell">
        <div className="ownership-card">
          <div className="ownership-card-header">
            <span
              className={`ownership-status-badge ${statusBadgeClass(
                request.status
              )}`}
            >
              {request.status.replace(/_/g, " ")}
            </span>
            <span className="ownership-request-id">
              Request <code>{request.req_id}</code>
            </span>
          </div>

          <div className="ownership-card-body">
            {error && (
              <div className="ownership-alert ownership-alert--error">
                {error}
              </div>
            )}

            <p className="ownership-section-title">Nomination</p>

            <div className="ownership-transfer">
              <div className="ownership-owner-box ownership-owner-box--from">
                <div className="ownership-owner-label">Current owner</div>
                <div className="ownership-owner-email">{currentOwner}</div>
              </div>
              <div className="ownership-arrow" aria-hidden="true">
                →
              </div>
              <div className="ownership-owner-box ownership-owner-box--to">
                <div className="ownership-owner-label">You (proposed)</div>
                <div className="ownership-owner-email">
                  {request.new_owner_email}
                </div>
              </div>
            </div>

            <div className="ownership-tagguid">
              <div className="ownership-tagguid-label">Tagguid</div>
              <div className="ownership-tagguid-value">{request.tagguid}</div>
            </div>

            <div className="ownership-meta-grid">
              <div className="ownership-meta-item">
                <span className="ownership-meta-label">Requested by</span>
                <span className="ownership-meta-value">
                  {request.requester_email}
                </span>
              </div>
              <div className="ownership-meta-item">
                <span className="ownership-meta-label">
                  Manager (next step)
                </span>
                <span className="ownership-meta-value">
                  {request.approver_email}
                </span>
              </div>
              <div className="ownership-meta-item">
                <span className="ownership-meta-label">Your sign-in</span>
                <span className="ownership-meta-value">
                  {email || "Loading…"}
                </span>
              </div>
              <div className="ownership-meta-item">
                <span className="ownership-meta-label">Expires</span>
                <span className="ownership-meta-value">
                  {new Date(request.expires_at * 1000).toLocaleString()}
                </span>
              </div>
            </div>

            {notYou && (
              <div className="ownership-alert ownership-alert--error">
                You are signed in as <strong>{email}</strong>, but this
                nomination is for <strong>{request.new_owner_email}</strong>.
              </div>
            )}

            {request.status !== "AWAITING_NOMINEE" && (
              <div className="ownership-alert ownership-alert--info">
                This nomination is no longer awaiting your response (status:{" "}
                {request.status}).
              </div>
            )}

            <div className="ownership-note-section">
              <label htmlFor="nominee-note">Note to requester (optional)</label>
              <textarea
                id="nominee-note"
                className="ownership-note-input"
                value={note}
                onChange={e => setNote(e.target.value)}
                disabled={!canAct}
              />
            </div>

            <div className="ownership-actions">
              <Button
                className="ownership-btn-approve"
                onClick={() => submit(true)}
                disabled={!canAct}
              >
                Accept ownership
              </Button>
              <Button
                className="ownership-btn-reject"
                onClick={() => submit(false)}
                disabled={!canAct}
              >
                Decline
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect(mapProps)(
  requireLogin("Ownership nomination")(OwnershipNomineePage)
);
